/**
 * Registry of the user-facing AI jobs Zunelo runs, and the pure merge that
 * turns per-user Settings into the route one job will actually take.
 *
 * Both sides read this file: `apps/api` to build a prompt and pick a
 * provider, `apps/web` to render Settings → IA and show the effective route
 * per task. Keeping it in `@zunelo/shared` is what stops the SPA from
 * guessing (and drifting from) what the API will do.
 */
import { sha256 } from './sha256.js';
import { STATIC_AI_MODEL_OPTIONS } from './settings.js';
export const AI_TASK_IDS = [
    'threadSummary',
    'threadReplyDraft',
    'threadAsk',
    'jiraTicketSummary',
    'workspaceAsk',
    'pullRequestReview',
];
export const AI_EFFORTS = ['low', 'medium', 'high'];
export const AI_TASKS = {
    threadSummary: {
        id: 'threadSummary',
        label: 'Resumen de hilos',
        description: 'Resume hilos de Slack en las tarjetas del radar.',
        defaultSystemPrompt: 'Eres un asistente que resume hilos de Slack para el inbox personal de una persona de ingeniería. Prioriza el estado actual, las decisiones tomadas y los bloqueos. No inventes datos ni atribuyas acciones que no aparezcan en los mensajes.',
        defaultEffort: 'low',
        toneSource: 'task',
    },
    threadReplyDraft: {
        id: 'threadReplyDraft',
        label: 'Borradores de respuesta',
        description: 'Redacta borradores de respuesta para hilos de Slack.',
        defaultSystemPrompt: 'Eres un asistente que redacta borradores de respuesta en Slack en primera persona, listos para enviar, sin preámbulos ni firmas. Respetas la idea del autor en vez de resumir el hilo.',
        defaultEffort: 'low',
        toneSource: 'slackVoice',
    },
    threadAsk: {
        id: 'threadAsk',
        label: 'Preguntas sobre un hilo',
        description: 'Responde preguntas usando el contexto de un hilo de Slack.',
        defaultSystemPrompt: 'Eres un asistente que responde preguntas usando únicamente el contexto de Slack que se te entrega. Si la respuesta no está en el contexto, lo dices con claridad en vez de suponerla.',
        defaultEffort: 'low',
        toneSource: 'task',
    },
    jiraTicketSummary: {
        id: 'jiraTicketSummary',
        label: 'Resumen de tickets de Jira',
        description: 'Resume un ticket de Jira y propone el siguiente paso.',
        defaultSystemPrompt: 'Eres un asistente que resume tickets de Jira para el inbox personal de una persona de ingeniería, destacando el estado actual y el bloqueo o siguiente paso cuando es evidente.',
        defaultEffort: 'low',
        toneSource: 'task',
    },
    workspaceAsk: {
        id: 'workspaceAsk',
        label: 'Asistente de contextos',
        description: 'Responde sobre los contextos de trabajo adjuntos en el radar.',
        defaultSystemPrompt: 'Eres el Asistente IA de Zunelo Radar. Respondes usando únicamente el briefing de contextos adjuntos, sin inventar tickets, pull requests ni personas.',
        defaultEffort: 'low',
        toneSource: 'task',
    },
    pullRequestReview: {
        id: 'pullRequestReview',
        label: 'Revisión de pull requests',
        description: 'Revisa el diff de un pull request y devuelve hallazgos.',
        defaultSystemPrompt: 'Eres un revisor senior de código. Buscas bugs y errores lógicos, regresiones, problemas de seguridad, concurrencia, manejo de errores, casos borde y tests ausentes. No comentas estilo trivial ni describes lo que hace el código.',
        defaultEffort: 'medium',
        toneSource: 'task',
    },
};
export function isAiTaskId(value) {
    return typeof value === 'string' && AI_TASK_IDS.includes(value);
}
export function isAiEffort(value) {
    return typeof value === 'string' && AI_EFFORTS.includes(value);
}
/** Longest model id any provider is expected to publish (`provider/name:tag`). */
export const MAX_AI_MODEL_ID_LENGTH = 80;
/**
 * Shape check, deliberately not an allowlist: providers add and retire models
 * weekly, and a frozen list is exactly what made the previous Settings model
 * dropdown unusable. An unknown-but-well-formed id reaches the provider and
 * fails with the provider's own message, which is diagnosable.
 */
export function isValidAiModelId(value) {
    if (typeof value !== 'string')
        return false;
    const trimmed = value.trim();
    if (!trimmed || trimmed.length > MAX_AI_MODEL_ID_LENGTH)
        return false;
    // `..` is legal in the charset but never appears in a real model id, and the
    // id is interpolated into Gemini's model path — reject it rather than rely
    // only on the caller's `encodeURIComponent`.
    if (trimmed.includes('..'))
        return false;
    return /^[A-Za-z0-9._:/-]+$/.test(trimmed);
}
/** Max characters accepted for a user-authored task system prompt. */
export const MAX_AI_SYSTEM_PROMPT_LENGTH = 2000;
/**
 * Stable, non-reversible conversation id for a (user, task) pair. OpenCode Go
 * requires one (`x-opencode-session`) to route and cache a conversation; the
 * raw uid must never leave our infrastructure, and per-task separation keeps a
 * PR review from sharing a cache line with a Slack summary.
 */
export function aiTaskSessionSeed(uid, task) {
    return sha256(`${uid?.trim() || 'service'}|${task}`).slice(0, 32);
}
/**
 * Session id for a connection probe. Kept apart from the task seeds so a
 * throwaway probe prompt never lands in a real task's prompt cache.
 */
export function aiProbeSessionSeed(uid) {
    return sha256(`${uid?.trim() || 'service'}|connection-probe`).slice(0, 32);
}
/**
 * First known model of a provider, used when a task selects a provider but no
 * model of its own. Inheriting the global model there would hand, say, Gemini
 * an OpenCode Go model id — a route Settings would display as valid and the
 * provider would reject on first use.
 */
export function defaultModelForProvider(provider) {
    return STATIC_AI_MODEL_OPTIONS[provider][0];
}
export function resolveTaskModel(override, global) {
    const candidate = override?.model?.trim();
    if (candidate && isValidAiModelId(candidate))
        return candidate;
    const provider = override?.provider;
    if (provider && provider !== global.provider)
        return defaultModelForProvider(provider);
    return global.model;
}
function pickFallback(override, global) {
    if (override?.fallbackProvider === 'none')
        return { fallbackProvider: 'none' };
    if (override?.fallbackProvider) {
        return {
            fallbackProvider: override.fallbackProvider,
            fallbackModel: override.fallbackModel?.trim() || defaultModelForProvider(override.fallbackProvider),
        };
    }
    return { fallbackProvider: global.fallbackProvider, fallbackModel: global.fallbackModel };
}
function pickTone(override, definition) {
    if (definition.toneSource !== 'task')
        return undefined;
    return override?.tone;
}
/**
 * Merge `global → task override → task default` into the route a single AI
 * call will take. Pure: the same inputs always describe the same call, which
 * is what lets Settings promise what the API will do.
 */
export function resolveAiTaskConfig(global, task) {
    const definition = AI_TASKS[task];
    const override = global.tasks?.[task];
    const prompt = override?.systemPrompt?.trim();
    return {
        task,
        provider: override?.provider ?? global.provider,
        model: resolveTaskModel(override, global),
        effort: override?.effort ?? global.effort ?? definition.defaultEffort,
        ...pickFallback(override, global),
        systemPrompt: prompt || definition.defaultSystemPrompt,
        tone: pickTone(override, definition),
        usesGlobalRoute: !override?.provider && !override?.model,
    };
}
//# sourceMappingURL=aiTasks.js.map